import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Undo2, Redo2, Save, Play, Download, Share2, CalendarClock,
  Home, ChevronRight, BarChart3, Sparkles, Columns3, Layers, Sigma, X, Check, Loader2,
  Eye, EyeOff, CopyPlus, ChevronDown, ChevronUp, Settings2, Copy,
  Activity, PieChart, TrendingUp, Hash, Plus, GripHorizontal,
  Filter as FilterIcon, Database, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
// Wedge 5: no client-side engine imports. Execution is server-side
// (reportsApi.run / reportsApi.preview) and chart data is read from
// result.chartData (pre-computed by the engine).
import { reportsApi, createReportConfig, uid } from '../api/reportsApi';
import { useReportFolders } from '../../analytics-shared/hooks/useFolders';
import { useObject } from '../../analytics-shared/hooks/useFieldCatalog';
import { useQuery, invalidateQueries } from '../../analytics-shared/hooks/queryShim';
import { metadataApi } from '../../analytics-shared/api/metadataApi';
import { displayLabel } from '../../analytics-shared/engine/reportEngine';
import { exportsApi } from '../../analytics-shared/api/exportsApi';
import FieldsPanel from '../../analytics-shared/components/FieldsPanel';
import DataSourcePicker, { summarySentence } from '../../analytics-shared/components/DataSourcePicker';
import FilterBuilder from '../../analytics-shared/components/FilterBuilder';
import ChartRenderer from '../../analytics-shared/components/ChartRenderer';
import ReportTable from '../components/ReportTable';
import ChartBuilder from '../components/ChartBuilder';
import AiPanel from '../components/AiPanel';
import ScreenFlowsPanel from '../components/ScreenFlowsPanel';
import SaveReportModal from '../components/modals/SaveReportModal';
import ScheduleReportModal from '../components/modals/ScheduleReportModal';

const AGG_CYCLE = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'];

const CHART_TYPE_OPTS = [
  { v: 'bar',     icon: BarChart3,  label: 'Bar' },
  { v: 'line',    icon: TrendingUp, label: 'Line' },
  { v: 'area',    icon: Activity,   label: 'Area' },
  { v: 'combo',   icon: Activity,   label: 'Combo (bar + line)' },
  { v: 'pie',     icon: PieChart,   label: 'Pie' },
  { v: 'donut',   icon: PieChart,   label: 'Donut' },
  { v: 'funnel',  icon: FilterIcon, label: 'Funnel' },
  { v: 'kpi',     icon: Hash,       label: 'KPI' },
  { v: 'heatmap', icon: Layers,     label: 'Heatmap' },
];

const DropZone = ({ zone, icon: Icon, label, empty, dropTarget, setDropTarget, onDropZone, children }) => (
  <div
    onDragOver={(e) => { e.preventDefault(); setDropTarget(zone); }}
    onDragLeave={() => setDropTarget((t) => (t === zone ? null : t))}
    onDrop={() => onDropZone(zone)}
    className={`flex-1 min-w-0 rounded-lg border-2 border-dashed px-2 py-1.5 transition-colors ${
      dropTarget === zone ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-slate-50'
    }`}
  >
    <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="flex flex-wrap gap-1 min-h-[26px] items-center">
      {children}
      {empty && <span className="text-[11px] text-slate-400">Drag fields here</span>}
    </div>
  </div>
);

const Chip = ({ label, onRemove, onClick, accent, prefix, dragProps }) => (
  <span
    onClick={onClick}
    {...(dragProps || {})}
    className={`inline-flex items-center gap-1 text-xs pl-2 pr-1 py-1 rounded-md border ${
      accent ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200'
    } ${onClick ? 'cursor-pointer hover:opacity-90' : ''} ${dragProps ? 'cursor-grab active:cursor-grabbing' : ''}`}
  >
    {prefix && <span className={`text-[10px] ${accent ? 'text-indigo-200' : 'text-indigo-500'}`}>{prefix}</span>}
    {label}
    <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
      className={accent ? 'hover:bg-white/20 rounded' : 'hover:bg-slate-100 rounded'}>
      <X className="h-3 w-3" />
    </button>
  </span>
);

const ChartPreviewCard = ({ chart, result, fieldMap, onRemove, onDuplicate, onEdit, onTypeChange }) => {
  const contentRef = useRef(null);
  const [contentH, setContentH] = useState(140);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContentH(Math.max(40, entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Read the pre-computed chartData entry from the engine result —
  // carries data + breakdown seriesKeys + resolved goalValue.
  const entry = useMemo(
    () => (result?.chartData || []).find((c) => c?.id === chart.id) || {},
    [result, chart.id],
  );
  const data = entry.data || [];
  const valueLabel = chart.agg === 'COUNT'
    ? 'Count'
    : `${chart.agg} ${displayLabel(fieldMap[chart.valueField]) || ''}`;

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden group shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50/80 border-b border-slate-100 flex-shrink-0">
        <span className="text-[11px] font-semibold text-slate-700 flex-1 truncate">{chart.title}</span>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {CHART_TYPE_OPTS.map(({ v, icon: Icon, label }) => (
            <button key={v} onClick={() => onTypeChange(chart.id, v)} title={label}
              className={`p-1 rounded transition-all ${
                chart.type === v
                  ? 'bg-indigo-100 text-indigo-600 shadow-sm'
                  : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100'
              }`}>
              <Icon className="w-3 h-3" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 border-l border-slate-200 pl-1 ml-0.5">
          <button onClick={() => onEdit(chart.id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit in panel">
            <Settings2 className="w-3 h-3" />
          </button>
          <button onClick={() => onDuplicate(chart.id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Duplicate">
            <Copy className="w-3 h-3" />
          </button>
          <button onClick={() => onRemove(chart.id)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" title="Remove chart">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div ref={contentRef} className="flex-1 min-h-0 overflow-hidden p-2">
        <ChartRenderer type={chart.type} data={data} height={contentH} valueLabel={valueLabel}
          seriesKeys={entry.seriesKeys || null} stacked={!!chart.stacked} stack100={!!chart.stack100}
          goalValue={entry.goalValue} colorRules={chart.colorRules || null} />
      </div>
    </div>
  );
};

const ChartPreviewSection = ({
  charts, result, fieldMap, collapsed, panelH,
  onToggle, onResizeStart, onAdd, onTypeChange, onDuplicate, onRemove, onEdit,
}) => {
  if (collapsed) {
    return (
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-200">
        <BarChart3 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-slate-600">
          {charts.length} chart{charts.length !== 1 ? 's' : ''} hidden
        </span>
        <button onClick={onToggle}
          className="flex items-center gap-1 ml-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-0.5 rounded-lg hover:bg-indigo-50 transition-colors">
          <ChevronDown className="w-3 h-3" /> Show Charts
        </button>
      </div>
    );
  }
  return (
    <div className="relative flex-shrink-0 flex flex-col bg-white border-b border-slate-200 overflow-hidden" style={{ height: panelH }}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex-shrink-0">
        <div className="w-5 h-5 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-3 h-3 text-indigo-600" />
        </div>
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Chart Preview</span>
        <span className="text-[10px] text-slate-400 font-medium">
          {charts.length} chart{charts.length !== 1 ? 's' : ''}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={onAdd}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-0.5 rounded-lg hover:bg-indigo-50 transition-colors">
            <Plus className="w-3 h-3" /> Add Chart
          </button>
          <div className="w-px h-3.5 bg-slate-200" />
          <button onClick={onToggle} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" title="Collapse chart preview">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden p-3">
        <div className={`h-full grid gap-3 ${charts.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {charts.map((chart) => (
            <ChartPreviewCard key={chart.id} chart={chart} result={result} fieldMap={fieldMap}
              onTypeChange={onTypeChange} onDuplicate={onDuplicate} onRemove={onRemove} onEdit={onEdit} />
          ))}
        </div>
      </div>

      <div onMouseDown={onResizeStart}
        className="absolute bottom-0 left-0 right-0 h-2 cursor-row-resize flex items-center justify-center group/resize hover:bg-indigo-50/60 transition-colors"
        title="Drag to resize">
        <GripHorizontal className="w-5 h-3 text-slate-300 group-hover/resize:text-indigo-400 transition-colors" />
      </div>
    </div>
  );
};

const BuilderErrorState = ({ title = 'Something went wrong', message, onBack, onRetry }) => (
  <div className="h-full flex items-center justify-center bg-slate-50 px-6">
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
      <div className="mx-auto w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mb-3">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
      </div>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{message}</p>
      <div className="flex items-center justify-center gap-2 mt-5">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Reports
        </Button>
        {onRetry && (
          <Button size="sm" onClick={onRetry}>Retry</Button>
        )}
      </div>
    </div>
  </div>
);

const ReportBuilderPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [history, setHistory] = useState(null);
  const [hIndex, setHIndex] = useState(0);
  const [initError, setInitError] = useState(null);
  const config = history ? history[hIndex] : null;

  const [rightTab, setRightTab] = useState('filters');
  const [draggedField, setDraggedField] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveAsMode, setSaveAsMode] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState(0);
  const [livePreview, setLivePreview] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const [frozen, setFrozen] = useState(null);

  const [chartsCollapsed, setChartsCollapsed] = useState(false);
  const [chartPanelH, setChartPanelH] = useState(280);
  const resizeDragging = useRef(false);
  const resizeStartY = useRef(0);
  const resizeStartH = useRef(0);
  const resizeHandlersRef = useRef({});

  const { folders } = useReportFolders();
  const skipSave = useRef(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let initial = reportId ? await reportsApi.get(reportId) : null;
        if (!initial && reportId) {
          // The route carried a reportId that the store doesn't know about
          // (deleted record, foreign browser, etc.). Surface this clearly
          // rather than silently creating a new untitled report.
          if (cancelled) return;
          setInitError(`Report "${reportId}" wasn't found. It may have been deleted.`);
          return;
        }
        if (!initial) {
          // Draft handoff from the create modal — nothing is persisted
          // until the user explicitly saves.
          try {
            const draft = sessionStorage.getItem('cluvik.pendingDraft');
            if (draft) {
              initial = JSON.parse(draft);
              sessionStorage.removeItem('cluvik.pendingDraft');
            }
          } catch { /* fall through to a blank config */ }
        }
        if (!initial) {
          const objectKey = params.get('object') || 'deals';
          initial = createReportConfig(objectKey);
        }
        if (cancelled) return;
        setHistory([initial]);
        setHIndex(0);
        setInitError(null);
        skipSave.current = true;
        if (params.get('run') === '1') setTimeout(() => runReport(initial), 350);
        // Builder entry counts as "recent activity" for the report —
        // matches the dashboards module's markViewed semantics and
        // keeps the Recent sidebar populated whether the user opens
        // the viewer or the editor first.
        if (reportId && initial?.id) {
          reportsApi.markRun(initial.id).catch(() => {});
        }
      } catch (e) {
        if (cancelled) return;
        setInitError(e?.message || 'Failed to load report');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Live object metadata — replaces the previous fixture lookup which only
  // knew the 10 demo objects and would return null for real tenant object
  // keys, locking the page in the loading guard below.
  const {
    data: object,
    isLoading: objectLoading,
    error: objectError,
    refetch: refetchObject,
  } = useObject(config?.object);

  // Multi-object field catalog. When the report type has related[]
  // entries (e.g. "Accounts with Leads and Contacts"), the backend
  // returns one group per source object with namespaced field keys —
  // the FieldsPanel renders this as Salesforce-style sections. Falls
  // back to single-object mode when the catalog is empty.
  const reportTypeKey = useMemo(
    () => (config?.reportType ? JSON.stringify({
      p: config.reportType.primary?.key,
      r: (config.reportType.related || []).map((r) => ({
        k: r.key, v: r.via, d: r.direction, a: r.anchor, j: r.join,
      })),
    }) : null),
    [config?.reportType],
  );
  const fieldCatalogQuery = useQuery({
    queryKey: ['report-type-fields', reportTypeKey],
    queryFn: () => metadataApi.getReportTypeFields(config.reportType),
    enabled: !!(config?.reportType && reportTypeKey),
    staleTime: 60_000,
  });
  const fieldGroups = fieldCatalogQuery.data?.groups || null;

  // Object graph — powers the in-builder "Data sources" editor. Only
  // fetched once the dialog first opens; cached afterwards.
  const graphQuery = useQuery({
    queryKey: ['report-graph'],
    queryFn: () => metadataApi.getReportGraph(),
    enabled: sourcesOpen,
    staleTime: 60_000,
  });
  const sourceGraph = graphQuery.data || { objects: [], relations: {} };

  // The primary descriptor for the picker. Older reports created via
  // ?object= have no reportType — synthesize one from live metadata.
  const sourcePrimary = useMemo(() => {
    if (config?.reportType?.primary?.key) return config.reportType.primary;
    if (!config?.object) return null;
    return {
      key: config.object,
      label: object?.label || config.object,
      plural: object?.plural || object?.label || config.object,
    };
  }, [config?.reportType, config?.object, object]);


  // Field map: backend-provided when we have a multi-object catalog;
  // otherwise derive from the single-object metadata. Both shapes use
  // the same key=fieldDef structure so downstream consumers (engine,
  // formatters) don't have to branch.
  const fieldMap = useMemo(() => {
    const backendMap = fieldCatalogQuery.data?.field_map;
    if (backendMap && Object.keys(backendMap).length > 0) {
      return backendMap;
    }
    if (!object?.fields) return {};
    return object.fields.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});
  }, [fieldCatalogQuery.data, object]);

  // Live preview now hits the server. Debounce config changes so we
  // don't fire a /reports/run on every keystroke — 300ms gives the
  // user time to keep typing without the network flapping. The
  // debounced snapshot is what the run hook keys on.
  const [debouncedConfig, setDebouncedConfig] = useState(config);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedConfig(config), 300);
    return () => clearTimeout(t);
  }, [config]);

  // Keying on a JSON hash means edits to filters / columns / charts
  // invalidate the cache and trigger a fresh server run. queryShim's
  // built-in dedupe handles the case where two consumers ask for the
  // same config concurrently.
  const previewKey = useMemo(
    () => JSON.stringify(debouncedConfig || {}),
    [debouncedConfig],
  );
  const liveQuery = useQuery({
    queryKey: ['reports', 'preview', debouncedConfig?.object, previewKey],
    queryFn: () => reportsApi.preview(debouncedConfig),
    enabled: !!debouncedConfig?.object,
    staleTime: 5_000,
  });
  const liveResult = liveQuery.data || null;
  const recordsLoading = liveQuery.isFetching;
  const recordsError = liveQuery.error;
  const refetchRecords = liveQuery.refetch;
  const records = liveResult?.records || [];

  const result = livePreview ? liveResult : (frozen || liveResult);

  // No autosave: reports persist ONLY when the user clicks Save (or
  // Save & Run). We just track whether the in-memory config has drifted
  // from the last persisted state.
  const [persisted, setPersisted] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (reportId) setPersisted(true); }, [reportId]);
  useEffect(() => {
    if (!config) return;
    if (skipSave.current) { skipSave.current = false; return; }
    setDirty(true);
  }, [config]);

  // Surface record-fetch failures without blocking the builder — the user
  // can still edit the report configuration; we just couldn't pull live
  // data. They can hit "Run Report" to retry once the underlying issue is
  // resolved.
  const lastRecordsErrorRef = useRef(null);
  useEffect(() => {
    if (recordsError && recordsError !== lastRecordsErrorRef.current) {
      lastRecordsErrorRef.current = recordsError;
      toast.error(`Couldn't load records: ${recordsError.message || 'Unknown error'}`);
    }
    if (!recordsError) lastRecordsErrorRef.current = null;
  }, [recordsError]);

  const commit = useCallback((next) => {
    const truncated = (history || []).slice(0, hIndex + 1);
    const newHist = [...truncated, next];
    setHistory(newHist);
    setHIndex(newHist.length - 1);
  }, [history, hIndex]);

  const patch = useCallback((partial) => {
    commit({ ...config, ...partial });
  }, [config, commit]);

  // Applying a data-source change: update reportType AND drop any
  // columns/groupBy/summaries/filters/charts that referenced a removed
  // related object's namespaced fields — stale "contact.email" keys
  // would otherwise 404 against the new field catalog.
  const handleSourcesChange = useCallback((nextRelated) => {
    if (!sourcePrimary) return;
    const validPrefixes = new Set(nextRelated.map((r) => r.key));
    const keyOk = (k) => {
      if (!k || !k.includes('.')) return true;
      return validPrefixes.has(k.split('.')[0]);
    };
    const pruneFilters = (node) => {
      if (!node) return node;
      if (node.type === 'condition') return keyOk(node.field) ? node : null;
      const children = (node.children || []).map(pruneFilters).filter(Boolean);
      return { ...node, children };
    };
    const summaries = Object.fromEntries(
      Object.entries(config.summaries || {}).filter(([k]) => keyOk(k)),
    );
    commit({
      ...config,
      reportType: {
        ...(config.reportType || {}),
        id: config.reportType?.id || `guided/${sourcePrimary.key}`,
        kind: nextRelated.length ? 'cross' : 'single',
        primary: sourcePrimary,
        related: nextRelated,
        description: summarySentence(sourcePrimary, nextRelated),
      },
      columns: (config.columns || []).filter(keyOk),
      groupBy: (config.groupBy || []).filter(keyOk),
      summaries,
      filters: pruneFilters(config.filters) || config.filters,
      sort: config.sort && !keyOk(config.sort.field) ? null : config.sort,
      charts: (config.charts || []).filter(
        (c) => keyOk(c.groupField) && keyOk(c.valueField),
      ),
    });
  }, [config, commit, sourcePrimary]);

  const undo = () => hIndex > 0 && setHIndex(hIndex - 1);
  const redo = () => history && hIndex < history.length - 1 && setHIndex(hIndex + 1);

  const addColumn = (key) => {
    if (config.columns.includes(key)) { toast('Field already in columns'); return; }
    patch({ columns: [...config.columns, key] });
  };
  const removeColumn = (key) => patch({ columns: config.columns.filter((k) => k !== key) });
  const reorderColumns = (from, to) => {
    const cols = [...config.columns];
    const summaryOnly = Object.keys(config.summaries).filter((k) => !config.columns.includes(k));
    const display = [...cols, ...summaryOnly];
    const [moved] = display.splice(from, 1);
    display.splice(to, 0, moved);
    patch({ columns: display.filter((k) => !summaryOnly.includes(k) || config.columns.includes(k)) });
  };
  const addGroup = (key) => {
    if (config.groupBy.includes(key)) { toast('Field already grouped'); return; }
    if (config.groupBy.length >= 3) { toast.error('Up to 3 grouping levels'); return; }
    patch({ groupBy: [...config.groupBy, key] });
  };
  const removeGroup = (key) => patch({ groupBy: config.groupBy.filter((k) => k !== key) });
  const addSummary = (key) => {
    const field = fieldMap[key];
    if (!field || !['number', 'currency', 'percent'].includes(field.type)) {
      toast.error('Only numeric fields can be summarized');
      return;
    }
    patch({ summaries: { ...config.summaries, [key]: config.summaries[key] || 'SUM' } });
  };
  const cycleSummary = (key) => {
    const cur = config.summaries[key];
    const next = AGG_CYCLE[(AGG_CYCLE.indexOf(cur) + 1) % AGG_CYCLE.length];
    patch({ summaries: { ...config.summaries, [key]: next } });
  };
  const removeSummary = (key) => {
    const s = { ...config.summaries };
    delete s[key];
    patch({ summaries: s });
  };
  const onSortColumn = (key) => {
    const dir = config.sort && config.sort.field === key && config.sort.dir === 'asc' ? 'desc' : 'asc';
    patch({ sort: { field: key, dir } });
  };

  const handleDrop = (zone) => {
    setDropTarget(null);
    if (draggedChip) {
      // Chip dropped on the zone background → move it to the end.
      if (draggedChip.zone === zone) {
        if (zone === 'columns') reorderColumns(draggedChip.index, config.columns.length - 1);
        if (zone === 'groupBy') reorderGroups(draggedChip.index, config.groupBy.length - 1);
      }
      setDraggedChip(null);
      return;
    }
    if (!draggedField) return;
    if (zone === 'columns') addColumn(draggedField.key);
    else if (zone === 'groupBy') addGroup(draggedField.key);
    else if (zone === 'summarize') addSummary(draggedField.key);
    setDraggedField(null);
  };

  // Drag-to-reorder for the chips inside the Columns / Group By zones.
  const [draggedChip, setDraggedChip] = useState(null);
  const reorderGroups = (from, to) => {
    if (from === to) return;
    const next = [...config.groupBy];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    patch({ groupBy: next });
  };
  const chipDragProps = (zone, index) => ({
    draggable: true,
    onDragStart: (e) => {
      e.stopPropagation();
      setDraggedChip({ zone, index });
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedChip || draggedChip.zone !== zone) { setDraggedChip(null); return; }
      if (zone === 'columns') reorderColumns(draggedChip.index, index);
      if (zone === 'groupBy') reorderGroups(draggedChip.index, index);
      setDraggedChip(null);
    },
    onDragEnd: () => setDraggedChip(null),
  });

  // "Run Report" now leaves the builder for the clean, full-width run
  // page (viewer). The builder auto-saves, so we flush the latest config
  // first, then navigate — the viewer executes the FULL (non-preview)
  // run with exact totals.
  const runReport = async (cfg) => {
    const target = cfg || config;
    if (!persisted || dirty) {
      // Unsaved work: route through the Save modal first (it offers
      // Save & Run). Export/Schedule/Share live on the run page only.
      setSaveAsMode(false);
      setSaveOpen(true);
      return;
    }
    setRunning(true);
    try {
      reportsApi.markRun(target.id).catch(() => {});
      navigate(`/reports/view/${target.id}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to run report');
    } finally {
      setRunning(false);
    }
  };

  const handleSaveMeta = async (meta, runAfter) => {
    let next = { ...config, ...meta };
    if (saveAsMode) {
      next = { ...next, id: uid('rep'), name: `${meta.name}`, lastRun: null };
    }
    commit(next);
    await reportsApi.save(next);
    setSavedAt(new Date());
    setPersisted(true);
    setDirty(false);
    skipSave.current = true;
    invalidateQueries(['reports']);
    setSaveOpen(false);
    toast.success(saveAsMode ? 'Saved as new report' : 'Report saved');
    if (saveAsMode) { setSaveAsMode(false); navigate(`/reports/builder/${next.id}`); }
    if (runAfter) setTimeout(() => runReport(next), 200);
  };

  // The modal owns the save call now (PUT /v1/reports/:id/schedule).
  // We just patch local config + close. No toast either — the modal
  // already fires one with the resolved next_run timestamp.
  const handleSchedule = (schedule) => {
    patch({ schedule });
    setScheduleOpen(false);
  };

  const handleExport = async () => {
    const n = await exportsApi.exportReportCsv(config);
    toast.success(`Exported ${n} rows to CSV`);
  };
  const handleShare = () => {
    try { navigator.clipboard.writeText(window.location.href); } catch (e) { /* ignore */ }
    toast.success('Shareable link copied to clipboard');
  };

  const addDefaultChart = () => {
    const groupField = config.groupBy[0] || (object.fields.find((f) => f.type === 'select') || {}).key;
    const numField = object.fields.find((f) => ['currency', 'number', 'percent'].includes(f.type));
    patch({
      charts: [...(config.charts || []), {
        id: uid('ch'), type: 'bar', groupField,
        valueField: numField ? numField.key : '', agg: numField ? 'SUM' : 'COUNT', title: 'New Chart',
      }],
    });
    setRightTab('charts');
    setChartsCollapsed(false);
    toast.success('Chart added');
  };

  const handleChartTypeChange = useCallback((chartId, newType) => {
    patch({ charts: (config.charts || []).map((c) => c.id === chartId ? { ...c, type: newType } : c) });
  }, [config, patch]);

  const handleChartDuplicate = useCallback((chartId) => {
    const chart = (config.charts || []).find((c) => c.id === chartId);
    if (!chart) return;
    patch({ charts: [...(config.charts || []), { ...chart, id: uid('ch'), title: `${chart.title} (Copy)` }] });
    toast.success('Chart duplicated');
  }, [config, patch]);

  const handleChartRemove = useCallback((chartId) => {
    patch({ charts: (config.charts || []).filter((c) => c.id !== chartId) });
  }, [config, patch]);

  const handleChartEdit = useCallback(() => setRightTab('charts'), []);

  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    resizeDragging.current = true;
    resizeStartY.current = e.clientY;
    resizeStartH.current = chartPanelH;

    resizeHandlersRef.current.move = (ev) => {
      if (!resizeDragging.current) return;
      const delta = ev.clientY - resizeStartY.current;
      setChartPanelH(Math.max(140, Math.min(600, resizeStartH.current + delta)));
    };
    resizeHandlersRef.current.up = () => {
      resizeDragging.current = false;
      window.removeEventListener('mousemove', resizeHandlersRef.current.move);
      window.removeEventListener('mouseup', resizeHandlersRef.current.up);
    };
    window.addEventListener('mousemove', resizeHandlersRef.current.move);
    window.addEventListener('mouseup', resizeHandlersRef.current.up);
  }, [chartPanelH]);

  const applyAiConfig = (partial) => commit({ ...config, ...partial });

  // Three distinct early-return states:
  //   1. Config still loading from storage — pure spinner, no chrome yet.
  //   2. Object metadata fetching — spinner with context.
  //   3. Object metadata failed to load OR the report points at an object
  //      that no longer exists in the tenant schema — actionable error so
  //      the user can go back / retry rather than stare at a dead spinner.
  if (initError) {
    return <BuilderErrorState message={initError} onBack={() => navigate('/reports')} />;
  }
  if (!config) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (objectLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50 gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        <p className="text-sm text-slate-500">Loading {config.object} schema…</p>
      </div>
    );
  }
  if (objectError) {
    return (
      <BuilderErrorState
        title="Couldn't load CRM schema"
        message={objectError.message || 'Failed to fetch object metadata'}
        onBack={() => navigate('/reports')}
        onRetry={refetchObject}
      />
    );
  }
  if (!object) {
    return (
      <BuilderErrorState
        title="Object not available"
        message={`This report points at "${config.object}", which isn't present in the current tenant schema. It may have been removed or renamed.`}
        onBack={() => navigate('/reports')}
      />
    );
  }

  const canUndo = hIndex > 0;
  const canRedo = history && hIndex < history.length - 1;
  const hasCharts = (config.charts || []).length > 0;

  return (
    <div className="h-full flex flex-col bg-slate-50 relative">
      <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <button onClick={() => navigate('/crm-platform')}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-600 hover:bg-slate-50 px-2 py-1.5 rounded-lg transition-colors"
          title="Back to CRM">
          <Home className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">CRM</span>
        </button>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <button onClick={() => navigate('/reports')} className="p-2 rounded-lg hover:bg-slate-100" title="Back to Reports">
          <ArrowLeft className="h-4 w-4 text-slate-600" />
        </button>

        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg bg-indigo-600">
            <Database className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <input
              value={config.name}
              onChange={(e) => setHistory((h) => {
                const copy = [...h]; copy[hIndex] = { ...copy[hIndex], name: e.target.value }; return copy;
              })}
              onBlur={(e) => patch({ name: e.target.value })}
              className="text-sm font-semibold text-slate-900 bg-transparent border-0 focus:outline-none focus:bg-slate-50 rounded px-1 -ml-1 w-56"
            />
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400 px-1">
              <button
                onClick={() => setSourcesOpen(true)}
                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                title="Edit data sources (related objects)"
              >
                <Database className="h-3 w-3" />
                {object.label}
                {(config.reportType?.related?.length || 0) > 0 && (
                  <span className="text-slate-400 font-normal">
                    +{config.reportType.related.length} related
                  </span>
                )}
              </button>
              <span>•</span>
              {!persisted
                ? <span className="text-amber-600 font-medium">Draft — not saved</span>
                : dirty
                  ? <span className="text-amber-600 font-medium">Unsaved changes</span>
                  : savedAt
                    ? <span className="flex items-center gap-0.5 text-emerald-600"><Check className="h-3 w-3" /> Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    : <span className="text-emerald-600">Saved</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-0.5 ml-2 border-l border-slate-200 pl-2">
          <button onClick={undo} disabled={!canUndo} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" title="Undo">
            <Undo2 className="h-4 w-4 text-slate-600" />
          </button>
          <button onClick={redo} disabled={!canRedo} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" title="Redo">
            <Redo2 className="h-4 w-4 text-slate-600" />
          </button>
        </div>

        <button onClick={() => setLivePreview((v) => !v)}
          className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-all ${
            livePreview ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-500'
          }`} title="Toggle live preview">
          {livePreview ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {livePreview ? 'Live' : 'Paused'}
        </button>

        <div className="flex items-center gap-1 ml-auto">
          <Button variant="ghost" size="sm" onClick={() => setRightTab('ai')} className="h-8 text-indigo-600">
            <Sparkles className="h-4 w-4 mr-1" /> AI Insights
          </Button>
          <Button variant="ghost" size="sm" onClick={addDefaultChart} className="h-8">
            <BarChart3 className="h-4 w-4 mr-1" /> Add Chart
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <Save className="h-4 w-4 mr-1" /> Save <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setSaveAsMode(false); setSaveOpen(true); }}>
                <Save className="h-4 w-4 mr-2" /> Save
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setSaveAsMode(true); setSaveOpen(true); }}>
                <CopyPlus className="h-4 w-4 mr-2" /> Save As New
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={() => runReport()} className="h-8 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 border-0 shadow-sm shadow-indigo-200">
            <Play className="h-4 w-4 mr-1" /> Run Report
          </Button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        <div className="w-64 border-r border-slate-200 bg-white flex-shrink-0">
          <FieldsPanel
            object={object} groups={fieldGroups} config={config}
            onAddColumn={addColumn} onAddGroup={addGroup} onAddSummary={addSummary}
            onDragField={setDraggedField}
          />
        </div>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex gap-2 p-2.5 bg-white border-b border-slate-200 flex-shrink-0">
            <DropZone zone="columns" icon={Columns3} label="Columns" empty={config.columns.length === 0}
              dropTarget={dropTarget} setDropTarget={setDropTarget} onDropZone={handleDrop}>
              {config.columns.map((k, i) => (
                <Chip key={k} label={displayLabel(fieldMap[k]) || k} onRemove={() => removeColumn(k)}
                  dragProps={chipDragProps('columns', i)} />
              ))}
            </DropZone>
            <DropZone zone="groupBy" icon={Layers} label={`Group By${config.groupBy.length ? ` (${config.groupBy.length})` : ''}`}
              empty={config.groupBy.length === 0}
              dropTarget={dropTarget} setDropTarget={setDropTarget} onDropZone={handleDrop}>
              {config.groupBy.map((k, i) => (
                <Chip key={k} prefix={i + 1} accent label={displayLabel(fieldMap[k]) || k} onRemove={() => removeGroup(k)}
                  dragProps={chipDragProps('groupBy', i)} />
              ))}
            </DropZone>
            <DropZone zone="summarize" icon={Sigma} label="Summarize" empty={Object.keys(config.summaries).length === 0}
              dropTarget={dropTarget} setDropTarget={setDropTarget} onDropZone={handleDrop}>
              {Object.entries(config.summaries).map(([k, agg]) => (
                <Chip key={k} prefix={agg} label={displayLabel(fieldMap[k]) || k}
                  onClick={() => cycleSummary(k)} onRemove={() => removeSummary(k)} />
              ))}
            </DropZone>
          </div>

          {hasCharts && (
            <ChartPreviewSection
              charts={config.charts}
              result={result} fieldMap={fieldMap}
              collapsed={chartsCollapsed} panelH={chartPanelH}
              onToggle={() => setChartsCollapsed((v) => !v)}
              onResizeStart={onResizeStart}
              onAdd={addDefaultChart}
              onTypeChange={handleChartTypeChange}
              onDuplicate={handleChartDuplicate}
              onRemove={handleChartRemove}
              onEdit={handleChartEdit}
            />
          )}

          {result?.meta?.preview && result?.meta?.truncated && (
            <div className="flex-shrink-0 px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
              Preview shows a sample of the first {(result.meta.rowLimit || 0).toLocaleString()} records
              ({(result.meta.totalPrimaryRecords || 0).toLocaleString()} total).
              Click <span className="font-semibold">Run Report</span> for exact results.
            </div>
          )}
          <div className="flex-1 min-h-0 bg-white">
            <ReportTable
              config={config} result={result} fieldMap={fieldMap}
              onSort={onSortColumn}
              onRemoveColumn={removeColumn}
              onReorderColumns={reorderColumns}
            />
          </div>
        </div>

        <div className="w-80 border-l border-slate-200 bg-white flex flex-col flex-shrink-0">
          <div className="flex border-b border-slate-200">
            {[
              { key: 'filters', label: 'Filters' },
              { key: 'charts', label: 'Charts' },
              { key: 'flows', label: 'Flows' },
              { key: 'ai', label: 'AI' },
            ].map((t) => (
              <button key={t.key} onClick={() => setRightTab(t.key)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  rightTab === t.key
                    ? 'text-indigo-700 border-b-2 border-indigo-600 bg-indigo-50/50'
                    : 'text-slate-500 hover:text-slate-700'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0">
            {rightTab === 'filters' && (
              <FilterBuilder object={object} filters={config.filters} fieldMap={fieldMap}
                onChange={(filters) => patch({ filters })}
                scope={config.scope || 'all'}
                onScopeChange={(scope) => patch({ scope })} />
            )}
            {rightTab === 'charts' && (
              <ChartBuilder object={object} config={config} fieldMap={fieldMap}
                result={result} onChange={(charts) => patch({ charts })} />
            )}
            {rightTab === 'flows' && (
              <ScreenFlowsPanel config={config}
                onChange={(screenFlows) => patch({ screenFlows })} />
            )}
            {rightTab === 'ai' && (
              <AiPanel result={result} config={config} fieldMap={fieldMap} onApplyConfig={applyAiConfig} />
            )}
          </div>
        </div>
      </div>

      {running && (
        <div className="absolute inset-0 z-40 bg-white/85 backdrop-blur-sm flex flex-col items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 w-80 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
            <h3 className="text-base font-semibold text-slate-900 mt-3">Running Report</h3>
            <p className="text-xs text-slate-500 mt-1">
              {runProgress < 40 ? 'Querying records...' : runProgress < 80 ? 'Applying filters & grouping...' : 'Rendering results...'}
            </p>
            <div className="h-2 bg-slate-100 rounded-full mt-4 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-600 to-violet-600 transition-all duration-150" style={{ width: `${Math.min(runProgress, 100)}%` }} />
            </div>
          </div>
        </div>
      )}

      <Dialog open={sourcesOpen} onOpenChange={(v) => !v && setSourcesOpen(false)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="px-6 py-4 border-b border-slate-200">
            <DialogTitle className="text-lg text-slate-900">Data Sources</DialogTitle>
            <DialogDescription>
              Add or remove related objects. Removing an object also removes any
              columns, filters, and charts that use its fields.
            </DialogDescription>
          </DialogHeader>
          <div className="p-5 max-h-[60vh] overflow-y-auto">
            {graphQuery.isLoading ? (
              <div className="py-10 flex items-center justify-center text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading relationships…
              </div>
            ) : (
              <DataSourcePicker
                graph={sourceGraph}
                primary={sourcePrimary}
                related={config.reportType?.related || []}
                onChange={handleSourcesChange}
              />
            )}
          </div>
          <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end">
            <Button onClick={() => setSourcesOpen(false)} className="bg-indigo-600 hover:bg-indigo-700">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SaveReportModal
        open={saveOpen}
        onClose={() => { setSaveOpen(false); setSaveAsMode(false); }}
        config={config}
        folders={folders}
        onSave={handleSaveMeta}
      />
      <ScheduleReportModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        schedule={config.schedule}
        reportId={config.id}
        onSaved={handleSchedule}
      />
    </div>
  );
};

export default ReportBuilderPage;
